import { AdZone } from 'models/Ads'
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import adsService from 'services/adsService'

type AdsContextValue = {
    prefetch: (zones: string[]) => Promise<void>
    getZone: (code: string) => AdZone | undefined
    hasNoAd: (code: string) => boolean
}

const AdsContext = createContext<AdsContextValue | undefined>(undefined)

export function useAds(): AdsContextValue {
    const ctx = useContext(AdsContext)
    if (!ctx) throw new Error('useAds must be used within AdsProvider')
    return ctx
}

export function AdsProvider({ children }: { children: React.ReactNode }) {
    const [byCode, setByCode] = useState<Record<string, AdZone>>({})
    const [noAdCodes, setNoAdCodes] = useState<Record<string, true>>({})
    const inflight = useRef<Record<string, Promise<void>>>({})

    const prefetch = useCallback(
        async (zones: string[]) => {
            const unique = Array.from(new Set((zones || []).filter(Boolean).map(z => z.trim())))

            const missing = unique.filter(z => !byCode[z] && !noAdCodes[z])
            if (missing.length === 0) return

            const key = missing.slice().sort().join('|')
            if (inflight.current[key]) return inflight.current[key]

            inflight.current[key] = (async () => {
                try {
                    const zonesData = await adsService.getAdsByZones(missing)

                    const receivedCodes = new Set(zonesData.map(z => z.code))

                    setByCode(prev => {
                        const next = { ...prev }
                        for (const z of zonesData) next[z.code] = z
                        return next
                    })

                    setNoAdCodes(prev => {
                        const next = { ...prev }
                        for (const asked of missing) {
                            if (!receivedCodes.has(asked)) next[asked] = true
                        }
                        return next
                    })
                } finally {
                    delete inflight.current[key]
                }
            })()

            return inflight.current[key]
        },
        [byCode, noAdCodes]
    )

    const getZone = useCallback((code: string) => byCode[code], [byCode])
    const hasNoAd = useCallback((code: string) => !!noAdCodes[code], [noAdCodes])

    const value = useMemo<AdsContextValue>(() => ({ prefetch, getZone, hasNoAd }), [prefetch, getZone, hasNoAd])

    return <AdsContext.Provider value={value}>{children}</AdsContext.Provider>
}